import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, DatePicker, Typography, Form, Spin, message } from "antd";
import styled from "styled-components";
import COLORS from "common/Colors";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { CALL_FEATCH_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// --- Styled Components (unchanged) ---

const LogContainer = styled.div`
  padding: 24px 0px;
  background: #fff;
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const StyledTable = styled(Table)`
  padding-top: 10px;
  table {
    width: 100% !important;
    border: 1px solid #ccc;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    color: ${COLORS.TEXT.PRIMARY};
    font-size: 14px;
  }
  .ant-table-row:nth-child(even) {
    background-color: #fafafa;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.TEXT.PRIMARY};
    white-space: nowrap;
    padding: 12px 16px !important;
    height: 48px !important;
  }
  .ant-table-tbody .ant-table-cell {
    padding: 12px 16px !important;
    height: 48px !important;
  }
  .ant-select-selector {
    min-width: 150px;
  }
`;

const ActionByCell = styled.div`
  display: flex;
  flex-direction: column;
  .name {
    font-weight: 500;
    color: #1e293b;
  }
  .timestamp {
    font-size: 12px;
    color: #64748b;
    font-style: italic;
  }
`;

const actionTypeOptions = [
	{ label: "Created", value: "Created" },
	{ label: "Updated", value: "Updated" },
	{ label: "Deleted", value: "Deleted" },
	{ label: "Approved", value: "Approved" },
	{ label: "Declined", value: "Declined" },
];

const Activitylog = ({ matterId, matterData }) => {
	const [form] = Form.useForm();
	const [tableData, setTableData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [dateRange, setDateRange] = useState(null);
	const [total, setTotal] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 10;
	const userList = useSelector(getUserList);

	const filterActionType = Form.useWatch("actionType", form) ?? [];
	const filterCategory = Form.useWatch("category", form) ?? [];
	const filterActionBy = Form.useWatch("actionBy", form) ?? [];

	const fetchLogs = useCallback(
		async (page = 1) => {
			if (!matterData?.matterId) return;
			setLoading(true);
			try {
				const params = new URLSearchParams({
					matterid: matterData?.matterId,
					page,
					limit: pageSize,
				});

				const res = await CALL_FEATCH_API(
					`insyts-matter/getactivitylog?${params.toString()}`,
					"GET"
				);

				const data = res?.response?.data ?? res?.data ?? res?.items ?? [];
				const totalCount =
					res?.response?.total ?? res?.total ?? res?.response?.count ?? res?.count ?? 0;

				setTableData(Array.isArray(data) ? data : []);
				setTotal(totalCount);
			} catch (err) {
				console.error("Activity log fetch error:", err);
				message.error("Failed to load activity log.");
			} finally {
				setLoading(false);
			}
		},
		[matterData?.matterId]  // ← only matterId, nothing else
	);

	useEffect(() => {
		setCurrentPage(1);
		fetchLogs(1);
	}, [fetchLogs]);

	const categoryOptions = useMemo(() => {
		const cats = new Set(tableData?.map((r) => r?.category)?.filter(Boolean));
		return [...cats].map((v) => ({ label: v, value: v }));
	}, [tableData]);

	const actionByOptions = useMemo(() => {
		const names = new Set();
		tableData.forEach((r) => {
			const actor = r.createdby || r?.updatedby || r?.deletedby || r?.approvedby || r?.declinedby || r?.actionby;
			if (actor) {
				const matched = (userList || []).find((u) => u.userid === actor || u.email === actor);
				names.add(matched?.fullname || matched?.name || actor);
			}
		});
		return [...names].map((v) => ({ label: v, value: v }));
	}, [tableData, userList]);

	const filteredData = useMemo(() => {
		return tableData.filter((r) => {
			// Action type filter
			if (filterActionType.length && !filterActionType.includes(r.type)) {
				return false;
			}

			// Category filter
			if (filterCategory.length && !filterCategory.includes(r.category)) {
				return false;
			}

			// Action by filter
			if (filterActionBy.length) {
				const actor =
					r.createdby || r.updatedby || r.deletedby ||
					r.approvedby || r.declinedby || r.actionby;
				const matched = (userList || []).find(
					(u) => u.userid === actor || u.email === actor
				);
				const displayName = matched?.fullname || matched?.name || actor;
				if (!filterActionBy.includes(displayName)) return false;
			}

			// Date range filter
			if (dateRange?.[0] && dateRange?.[1]) {
				const recordDate = r.createdat || r.updatedat || r.deletedat
					|| r.approvedat || r.declinedat;
				if (!recordDate) return false;
				const date = new Date(recordDate);
				const from = dateRange[0].startOf("day").toDate();
				const to = dateRange[1].endOf("day").toDate();
				if (date < from || date > to) return false;
			}

			return true;
		});
	}, [tableData, filterActionType, filterCategory, filterActionBy, userList, dateRange]);

	const columns = [
		{
			title: "Action by",
			key: "actionBy",
			render: (_, record) => {

				// ✅ This picks the correct action verb from either field
				const ACTION_VERBS = new Set(["Created", "Updated", "Deleted", "Approved", "Declined", "Cloned"]);
				const action = ACTION_VERBS.has(record.type) ? record.type : record.category;

				let emailOrId = "—";

				if (action === "Created" || action === "Cloned") {
					emailOrId = record.createdby;
				} else if (action === "Updated") {
					emailOrId = record.updatedby || record.createdby;
				} else if (action === "Deleted") {
					emailOrId = record.deletedby;
				} else if (action === "Approved") {
					emailOrId = record.approvedby || record.updatedby;
				} else if (action === "Declined") {
					emailOrId = record.declinedby;
				}

				const matchedUser = (userList || []).find(
					(u) => u.userid === emailOrId || u.email === emailOrId
				);
				const displayName = matchedUser?.fullname || matchedUser?.name || emailOrId || "—";

				const timestamp =
					action === "Updated" ? record.updatedat :
						action === "Deleted" ? record.deletedat :
							action === "Approved" ? (record.approvedat || record.updatedat) :
								action === "Declined" ? record.declinedat :
									record.createdat;

				return (
					<ActionByCell>
						<span className="name">{displayName}</span>
						<span className="timestamp">
							{timestamp ? new Date(timestamp).toLocaleDateString("en-GB", {
								day: "numeric", month: "short", year: "numeric"
							}) : "—"}
						</span>
					</ActionByCell>
				);
			},
		},
		{ title: "Type", dataIndex: "type", key: "type", render: (v) => v || "—" },
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			render: (v) => v || "—",
		},
		{
			title: "Action Made",
			dataIndex: "action",
			key: "action",
			render: (v) => v || "—",
		},
	];

	return (
		<LogContainer>
			<FilterHeader>
				<Text strong style={{ fontSize: "16px" }}>
					Total log: {filteredData?.length}
				</Text>

				<FilterGroup>
					<Form form={form} layout="vertical">
						<div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
							<div style={{ width: 176 }}>
								<DropdownCheckBox
									name="actionBy"
									label="Action by"
									options={actionByOptions}
									form={form}
									handleFormChange={() => setCurrentPage(1)}
								/>
							</div>
							<div style={{ width: 176 }}>
								<DropdownCheckBox
									name="actionType"
									label="Action Type"
									options={actionTypeOptions}
									form={form}
									handleFormChange={() => setCurrentPage(1)}
								/>
							</div>
							<div style={{ width: 176 }}>
								<DropdownCheckBox
									name="category"
									label="Category"
									options={categoryOptions}
									form={form}
									handleFormChange={() => setCurrentPage(1)}
								/>
							</div>
							<div style={{ marginBottom: 12 }}>
								<RangePicker
									style={{ borderRadius: "20px" }}
									value={dateRange}
									format="DD/MM/YYYY"
									onChange={(dates) => {
										setDateRange(dates);
										setCurrentPage(1);
									}}
								/>

							</div>
						</div>
					</Form>
				</FilterGroup>
			</FilterHeader>

			<Spin spinning={loading}>
				<StyledTable
					dataSource={filteredData}
					columns={columns}
					rowKey={(r, i) => r?.id || r?.activityid || `row-${i}`}
					pagination={{
						current: currentPage,
						pageSize: pageSize,
						total: filteredData?.length,
						showTotal: (t) => `Total ${t} records`,
						onChange: (page) => {
							setCurrentPage(page);
							fetchLogs(page);
						},
						showSizeChanger: false,
					}}
				/>
			</Spin>
		</LogContainer>
	);
};

export default Activitylog;
